import type { SnapshotId, SnapshotRequestShortcut } from '@ipfabric/utils';
import { useGlobalStateContextSelectors } from 'components/GlobalState/GlobalStateModel';
import { selectAuthenticatedAndLicensed } from 'components/GlobalState/selectors/selectAuthenticatedAndLicensed';
import { changeSnapshot } from 'pages/SnapshotManagement/actions';
import { LOADED_STATES } from 'pages/SnapshotManagement/constants';
import type { Snapshot } from 'pages/SnapshotManagement/snapshotManagementTypes';
import { useSnapshots } from 'pages/SnapshotManagement/useSnapshots';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useNotifications } from 'utils/hooks/useNotifications';

const SELECT_SNAPSHOT_QUERY_PARAMETER = 'selectSnapshot';

const getSnapshotToSelect = (
  snapshotToSelect: SnapshotId | SnapshotRequestShortcut | null,
  snapshots: Snapshot[],
): Snapshot | undefined => {
  switch (snapshotToSelect) {
    case null:
      return undefined;
    case '$last':
      return snapshots.find((s) => s.status === 'done');
    case '$prev':
      return snapshots.filter((s) => s.status === 'done')[1];
    case '$lastLocked':
      return snapshots.find((s) => s.status === 'done' && s.locked);
    default:
      return snapshots.find((s) => s.id === snapshotToSelect);
  }
};

export const useSnapshotFromUrl = (): { willUpdateSnapshot: boolean } => {
  const [searchParams, setSearchParams] = useSearchParams();
  const snapshotIdToSelect = searchParams.get(SELECT_SNAPSHOT_QUERY_PARAMETER);
  const snapshots = useSnapshots();
  const dispatch = useDispatch();
  const notifications = useNotifications();

  const globalStateCtxProps = useGlobalStateContextSelectors({
    authenticatedAndLicensed: selectAuthenticatedAndLicensed,
  });

  const targetSnapshot = getSnapshotToSelect(snapshotIdToSelect, snapshots);

  const targetSnapshotState: 'none' | 'invalid' | 'valid' = (() => {
    if (snapshotIdToSelect === null) {
      return 'none';
    }

    if (
      targetSnapshot === undefined ||
      !LOADED_STATES.includes(targetSnapshot.state)
    ) {
      return 'invalid';
    }

    return 'valid';
  })();

  useEffect(() => {
    if (
      targetSnapshotState === 'none' ||
      !globalStateCtxProps.authenticatedAndLicensed
    ) {
      return;
    }
    if (targetSnapshotState === 'valid' && targetSnapshot !== undefined) {
      dispatch(changeSnapshot(targetSnapshot.id));
    }
    if (targetSnapshotState === 'invalid') {
      notifications.showNotification({
        type: 'warning',
        message:
          'The snapshot requested in the URL is not loaded and cannot be selected.',
      });
    }

    // I didn't find any easy way to immutably update search params,
    // but at least verified that every call of useSearchParams produces a new instance.
    searchParams.delete(SELECT_SNAPSHOT_QUERY_PARAMETER);
    setSearchParams(searchParams, { replace: true });
  }, [
    targetSnapshot,
    targetSnapshotState,
    searchParams,
    setSearchParams,
    globalStateCtxProps.authenticatedAndLicensed,
  ]);

  return { willUpdateSnapshot: targetSnapshotState === 'valid' };
};
